/**
 * Banka sorgusu sonucunu UYAP'in banka haczi listesiyle (getBankaHacziParams_brd) eslestirir. (DEV-3513)
 *
 * UYAP kisi borcluda banka sorgusunu banka basina bir satir olarak donduruyor: {bankaAdi, eftKodu}.
 * 19.09.2026'dan beri kurum borcluyu da sorguluyor, ama hesap basina bir satir ve baska alanlarla:
 * bankaUnvan (bosluk dolgulu; bazen 20 karakterde kesik ya da ASCII: "YAPI VE KREDİ BANKAS",
 * "TURKIYE IS BANKASI A.S."), ibanHesapNo (IBAN ya da duz hesap no), subeAdi, hesapTuru... Ayni hesap
 * farkli tarih ve unvan yazimiyla birden cok kez gelebiliyor.
 *
 * Kurum satirlari burada kisi bicimine indirilir: banka basina bir satir, bankaAdi UYAP listesindeki
 * adin bankaKisaAd hali, hesaplar ayrica saklanir. Boylece sihirbaz, banka secici ve Excel iki borclu
 * turunde ayni calisir. Listede tek bankaya oturmayan satir eslesmedi=true isaretlenir ve varsayilan
 * talepte secilmez; yanlis bankaya haciz gitmesin, kullanici elle secsin.
 *
 * Bu modul "@/main"i cekmiyor; hem plugin (DosyaBorcluBankaBilgileri) hem uygulama (sihirbaz, banka
 * secici) kullaniyor.
 */

// BankaHacziParamsBankaRow'un burada kullanilan alanlari.
export interface UyapBankaSatiri {
    ilgili: string;
    ilgiliKisiIlkKisiKurumId: number;
}

export interface KurumBankaHesabi {
    bankaUnvan: string;
    ibanHesapNo: string;
    subeAdi: string;
    hesapTuru: string;
    dovizTuru: string;
    tarih: string;
    hesapDurumu: string;
    hesapOrtaklikDurumu: string;
    hesapOrtaklikYuzdesi: string;
}

// sorguSonucDVO satiri: kisi alanlari, kurumda ek olarak eslesmedi ve hesaplar.
export interface BankaSorguSatiri {
    bankaAdi: string;
    eftKodu: number | null;
    logo: Array<any>;
    logoBulunamadi: boolean;
    selected: boolean;
    eslesmedi?: boolean;
    hesaplar?: Array<KurumBankaHesabi>;
}

/** Sihirbaz ve banka secicinin UYAP banka adini kisi sonucundaki bankaAdi bicimine cevirdigi zincir. */
export function bankaKisaAd(ilgili: string): string {
    return String(ilgili || "")
        .replace("TÜRK ANONİM ŞİRKETİ", "T.A.Ş.")
        .replace("ANONİM ŞİRKETİ", "A.Ş.")
        .replace("TÜRK ANONİM ORTAKLIĞI", "T.A.O.")
        .replace("TÜRKİYE CUMHURİYETİ", "T.C.")
        .replace(" GENEL MÜDÜRLÜĞÜ", "");
}

/** Kurum cevabi: satirda bankaAdi yok, bankaUnvan ya da ibanHesapNo var. */
export function hesapBazliSatirMi(satir: any): boolean {
    return !!satir && typeof satir === "object" && !satir.bankaAdi
        && ("bankaUnvan" in satir || "ibanHesapNo" in satir);
}

/**
 * Sorgu satirlarindaki bankalari UYAP listesinde bulur. eslesen listenin kendi sirasiyla ve tekildir;
 * eslesmedi isaretli, adi bos ya da listede karsiligi olmayan satir varsa eslesmeyenVar=true olur.
 * Adla eslestirme sihirbaz ve secicinin DEV-3513 oncesi kuraliyla ayni (kisa ad, kucuk harf, birebir).
 */
export function sorguBankalariniBul<T extends UyapBankaSatiri>(bankalar: Array<T>, sorguSatirlari: unknown): { eslesen: Array<T>, eslesmeyenVar: boolean } {
    const satirlar: Array<any> = Array.isArray(sorguSatirlari) ? sorguSatirlari : [];
    const adlar: Array<string> = [];
    let eslesmeyenVar = false;
    satirlar.forEach((satir) => {
        const ad = satir && typeof satir.bankaAdi === "string" ? satir.bankaAdi.trim().toLocaleLowerCase("tr") : "";
        if (!ad || satir.eslesmedi === true)
            eslesmeyenVar = true;
        else
            adlar.push(ad);
    });
    const eslesen = (bankalar || []).filter((banka) => adlar.includes(bankaKisaAd(banka.ilgili).toLocaleLowerCase("tr")));
    const bulunanAdlar = eslesen.map((banka) => bankaKisaAd(banka.ilgili).toLocaleLowerCase("tr"));
    if (adlar.some((ad) => !bulunanAdlar.includes(ad)))
        eslesmeyenVar = true;
    return {eslesen, eslesmeyenVar};
}

// ---------- kurum unvani -> UYAP banka listesi ----------

const KISALTMALAR: { [kisaltma: string]: Array<string> } = {
    "AS": ["ANONIM", "SIRKETI"],
    "TAS": ["TURK", "ANONIM", "SIRKETI"],
    "TAO": ["TURK", "ANONIM", "ORTAKLIGI"],
    "TC": ["TURKIYE", "CUMHURIYETI"],
};
// Sondan atilan hukuki ekler; "TURK ANONIM ..." once denensin diye uzundan kisaya.
const SON_EKLER: Array<Array<string>> = [
    ["TURK", "ANONIM", "SIRKETI"], ["TURK", "ANONIM", "ORTAKLIGI"], ["ANONIM", "SIRKETI"], ["GENEL", "MUDURLUGU"],
];
const BAS_EKLER: Array<Array<string>> = [["TURKIYE", "CUMHURIYETI"], ["TURKIYE"]];
// Kesik unvanin onek sayilmasi icin en az bu kadar karakter olmali ("TURKIYE" tek basina yetmesin).
const MIN_ONEK = 8;

/** Buyuk harf, Turkce harfler ASCII, noktalama bosluk. */
export function katla(metin: string): string {
    return String(metin || "")
        .toLocaleUpperCase("tr")
        .replace(/İ/g, "I").replace(/Ş/g, "S").replace(/Ğ/g, "G")
        .replace(/Ü/g, "U").replace(/Ö/g, "O").replace(/Ç/g, "C")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
}

function kelimeler(ad: string): Array<string> {
    const ham = katla(ad).split(" ").filter((k) => k.length > 0);
    // "A S", "T A S", "T C" gibi noktalamadan dagilmis kisaltmalari birlestir.
    const birlesik: Array<string> = [];
    let tampon = "";
    ham.forEach((k) => {
        if (k.length === 1) {
            tampon += k;
            return;
        }
        if (tampon) {
            birlesik.push(tampon);
            tampon = "";
        }
        birlesik.push(k);
    });
    if (tampon)
        birlesik.push(tampon);
    const sonuc: Array<string> = [];
    birlesik.forEach((k, i) => {
        if (i === 0 && k === "T")
            sonuc.push("TURKIYE");              // "T.VAKIFLAR BANKASI T.A.O."
        else if (KISALTMALAR[k])
            sonuc.push(...KISALTMALAR[k]);
        else
            sonuc.push(k);
    });
    return sonuc;
}

function sondanAt(k: Array<string>): Array<string> {
    let sonuc = k.slice();
    let degisti = true;
    while (degisti) {
        degisti = false;
        for (const ek of SON_EKLER) {
            if (sonuc.length > ek.length && ek.every((e, i) => sonuc[sonuc.length - ek.length + i] === e)) {
                sonuc = sonuc.slice(0, sonuc.length - ek.length);
                degisti = true;
            }
        }
    }
    return sonuc;
}

function bastanAt(k: Array<string>): Array<string> {
    for (const ek of BAS_EKLER) {
        if (k.length > ek.length && ek.every((e, i) => k[i] === e))
            return k.slice(ek.length);
    }
    return k;
}

interface Anahtar {
    tam: string;
    cekirdek: string;
}

// [0] unvanin kendisi, [1] bastaki "TURKIYE (CUMHURIYETI)" atilmis hali ("GARANTI BANKASI" gibi yazimlar icin).
function anahtarlar(ad: string): Array<Anahtar> {
    const k = kelimeler(ad);
    const gevsek = bastanAt(k);
    return [
        {tam: k.join(" "), cekirdek: sondanAt(k).join(" ")},
        {tam: gevsek.join(" "), cekirdek: sondanAt(gevsek).join(" ")},
    ];
}

function kelimeOnekiMi(uzun: string, kisa: string): boolean {
    return kisa.length > 0 && (uzun === kisa || uzun.startsWith(kisa + " "));
}

/**
 * Unvani listede tek bir bankaya oturtur; bulamazsa ya da birden cok aday esit kalirsa null.
 * Sira: tam ya da cekirdek esitligi, sonra kesik unvanin onek olmasi ya da listedeki cekirdegin
 * unvanin kelime oneki olmasi; ayni kurallar sonra bastaki "TURKIYE" atilarak denenir.
 */
export function unvanIleBankaBul<T extends UyapBankaSatiri>(unvan: string, bankalar: Array<T>): T | null {
    const u = anahtarlar(unvan);
    if (!u[0].tam)
        return null;
    let enIyiler: Array<{ banka: T, skor: number, uzunluk: number }> = [];
    (bankalar || []).forEach((banka) => {
        if (!banka || typeof banka.ilgili !== "string")
            return;
        const b = anahtarlar(banka.ilgili);
        let skor = 0;
        let uzunluk = 0;
        for (let seviye = 0; seviye < 2 && skor === 0; seviye++) {
            const U = u[seviye], B = b[seviye];
            const ceza = seviye === 0 ? 0 : 1;
            if (!U.tam || !B.tam)
                continue;
            if (U.tam === B.tam || (U.cekirdek && U.cekirdek === B.cekirdek)) {
                skor = 3 - ceza;
                uzunluk = B.cekirdek.length;
            } else if (U.tam.length >= MIN_ONEK && B.tam.startsWith(U.tam)) {
                skor = 2 - ceza;                     // kesik unvan
                uzunluk = U.tam.length;
            } else if (B.cekirdek.length >= MIN_ONEK / 2 && kelimeOnekiMi(U.tam, B.cekirdek)) {
                skor = 2 - ceza;                     // unvanda fazladan kelime
                uzunluk = B.cekirdek.length;
            }
        }
        if (skor === 0)
            return;
        const enIyi = enIyiler[0];
        if (!enIyi || skor > enIyi.skor || (skor === enIyi.skor && uzunluk > enIyi.uzunluk))
            enIyiler = [{banka, skor, uzunluk}];
        else if (skor === enIyi.skor && uzunluk === enIyi.uzunluk)
            enIyiler.push({banka, skor, uzunluk});
    });
    return enIyiler.length === 1 ? enIyiler[0].banka : null;
}

function temiz(metin: unknown): string {
    return metin === null || metin === undefined ? "" : String(metin).replace(/\s+/g, " ").trim();
}

// "18.12.2019" -> 20191218; okunamazsa 0.
function tarihSayisi(tarih: string): number {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(tarih);
    return m ? Number(m[3] + m[2] + m[1]) : 0;
}

/**
 * Hesap bazli kurum satirlarini banka bazli, kisi uyumlu satirlara indirir. Ayni hesap (hesap no +
 * doviz) birden cok gelirse en yeni tarihli kalir. Sonuc bankaAdi'na gore sirali, eslesmeyenler sonda.
 */
export function kurumBankaSatirlariniGrupla(satirlar: unknown, bankalar: Array<UyapBankaSatiri>): Array<BankaSorguSatiri> {
    const gruplar: Array<{ anahtar: string, satir: BankaSorguSatiri, hesapIndeksi: { [hesap: string]: number } }> = [];
    (Array.isArray(satirlar) ? satirlar : []).forEach((ham: any) => {
        if (!ham || typeof ham !== "object")
            return;
        const unvan = temiz(ham.bankaUnvan);
        const hesapNo = temiz(ham.ibanHesapNo);
        if (!unvan && !hesapNo)
            return;
        const banka = unvan ? unvanIleBankaBul(unvan, bankalar) : null;
        const anahtar = banka ? "U" + banka.ilgiliKisiIlkKisiKurumId : "N" + (katla(unvan) || "BILINMEYEN");
        let grup = gruplar.find((g) => g.anahtar === anahtar);
        if (!grup) {
            grup = {
                anahtar,
                satir: {
                    bankaAdi: banka ? bankaKisaAd(banka.ilgili) : (unvan || "Bilinmeyen banka"),
                    eftKodu: null,
                    logo: [],
                    logoBulunamadi: true,
                    selected: !!banka,
                    eslesmedi: !banka,
                    hesaplar: [],
                },
                hesapIndeksi: {},
            };
            gruplar.push(grup);
        }
        const hesap: KurumBankaHesabi = {
            bankaUnvan: unvan,
            ibanHesapNo: hesapNo,
            subeAdi: temiz(ham.subeAdi),
            hesapTuru: temiz(ham.hesapTuru),
            dovizTuru: temiz(ham.dovizTuru),
            tarih: temiz(ham.tarih),
            hesapDurumu: temiz(ham.hesapDurumu),
            hesapOrtaklikDurumu: temiz(ham.hesapOrtaklikDurumu),
            hesapOrtaklikYuzdesi: temiz(ham.hesapOrtaklikYuzdesi),
        };
        const hesaplar = grup.satir.hesaplar as Array<KurumBankaHesabi>;
        const hesapAnahtari = hesapNo.replace(/\s+/g, "") + "|" + hesap.dovizTuru;
        const onceki = hesapNo ? grup.hesapIndeksi[hesapAnahtari] : undefined;
        if (onceki === undefined) {
            if (hesapNo)
                grup.hesapIndeksi[hesapAnahtari] = hesaplar.length;
            hesaplar.push(hesap);
        } else if (tarihSayisi(hesap.tarih) > tarihSayisi(hesaplar[onceki].tarih)) {
            hesaplar[onceki] = hesap;
        }
    });
    return gruplar.map((g) => g.satir).sort((a, b) =>
        (a.eslesmedi ? 1 : 0) - (b.eslesmedi ? 1 : 0) || a.bankaAdi.localeCompare(b.bankaAdi, "tr"));
}
